import { useEffect, useState } from 'react';
import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { Tooltip } from '@mui/material';
import { Account } from '../../../../../types';
import { DetailViewCard } from '../../../../Compounds/CardWithTitle';
import { PermissibleRender } from '../../../../Compounds/PermissibleRender';
import { BasicDataGrid } from '../../../../Compounds/Table/BasicDataGrid';
import { useSearchParams } from 'react-router-dom';
import { CardButton } from '../../../../Compounds/Buttons/CardButton';
import { AccountCustomizationData, useAccountCustomizationsStore } from '../../../../../store/AccountCustomization';
import { PostAccountCustomizationRequest } from '../../../../../Api/Preferences/AccountCustomizations/accountCustomizationsApi';
import { useSnackbar } from '../../../../../Context/SnackbarContext';
import { MultiSelect } from '../../../../Compounds/Selects/MultiSelect';
import { AccountData, getAccountSubject } from '../../../../../store/Accounts';
import { Permission } from '../../../../../store/GeneralStore';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../../../utils/styleHelpers';
import { TextSpan } from '../../../../Atoms/Text';
import { Icon } from '../../../../Atoms/Icon';

export interface SubAccountsParams {
  account: AccountData;
  accountCustomizations: AccountCustomizationData;
}

export const SubAccounts = ({ accountCustomizations, account }: SubAccountsParams) => {
  const [isSubAgentAccount, setIsSubAgentAccount] = useState<boolean>(account.isSubAgent);
  const [subAccounts, setSubAccounts] = useState<Account[]>(accountCustomizations?.content?.sub_accounts || []);
  const [params, setParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<Account[]>([]);
  const { updateRequest, createRequest, fetchAccountCustomizations } = useAccountCustomizationsStore();
  const { setSnack } = useSnackbar();

  useEffect(() => {
    const accountsSub = getAccountSubject().subscribe((a) => setAccounts(a));

    return () => {
      if (accountsSub) accountsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setSubAccounts(accountCustomizations?.content?.sub_accounts || []);
  }, [accountCustomizations.account_id]);

  useEffect(() => {
    setIsSubAgentAccount(account.isSubAgent);
  }, [account.id]);

  const handleDetailClick = (id: string) => {
    params.get('detailId') === id ? params.delete('detailId') : params.set('detailId', id);
    setParams(params, { replace: true });
  };

  const handleDeleteClick = async (id: string) => {
    const update: PostAccountCustomizationRequest = JSON.parse(JSON.stringify(accountCustomizations));
    update.content.sub_accounts = update?.content?.sub_accounts?.filter((acc) => acc?.id !== id);

    handleUpdate(update, 'removed');
  };

  const handleAccountSelect = (e: React.SyntheticEvent<Element, Event>, value: string[] | null) => {
    setSelectedAccounts(accounts.filter((a) => value?.includes(a.name)) || []);
  };

  const handleSave = () => {
    const update: PostAccountCustomizationRequest = JSON.parse(JSON.stringify(accountCustomizations));
    update.content.sub_accounts = [...(update?.content?.sub_accounts || []), ...selectedAccounts];

    handleUpdate(update, 'saved');
  };

  const handleUpdate = async (update: PostAccountCustomizationRequest, action: string) => {
    const res = accountCustomizations.id
      ? await updateRequest(accountCustomizations.id, update)
      : await createRequest(update);
    if (res?.error) {
      setSnack({
        message: res.error.message,
        open: true,
        type: 'error'
      });
    } else {
      setSnack({
        message: `Account ${action}`,
        open: true,
        type: 'success'
      });
      setSubAccounts(update?.content?.sub_accounts || []);
      setSelectedAccounts([]);

      await fetchAccountCustomizations();
    }
  };

  const getAccountOptions = () => {
    const filteredNames = accounts
      .filter((acct) => subAccounts.every((subAcct) => subAcct.id !== acct.id))
      .map((acct) => acct.name);
    return [...new Set(filteredNames)];
  };

  const addExistingSubAccounts = () => {
    setCollapsed(false);
    const matchingAccount = accounts.find((acct) => acct.id === account.id);
    if (isSubAgentAccount) {
      const accountsMatchingSubAgentIdNotYetAdded = accounts.filter((acct) => {
        return (
          (acct.primarySubAgentId === matchingAccount?.sfId || acct.secondarySubAgentId === matchingAccount?.sfId) &&
          !selectedAccounts.some((selAcct) => selAcct.id === acct.id) &&
          !subAccounts.some((subAcct) => subAcct.id === acct.id)
        );
      });
      setSelectedAccounts([...selectedAccounts, ...accountsMatchingSubAgentIdNotYetAdded]);
    } else {
      const accountsMatchingParentIdNotYetAdded = accounts.filter((acct) => {
        return (
          acct.parentId === matchingAccount?.sfId &&
          !selectedAccounts.some((selAcct) => selAcct.id === acct.id) &&
          !subAccounts.some((subAcct) => subAcct.id === acct.id)
        );
      });

      setSelectedAccounts([...selectedAccounts, ...accountsMatchingParentIdNotYetAdded]);
    }
  };

  const cardActions = () => (
    <PermissibleRender requiredPermissions={[Permission.CREATE_USERS]}>
      <Tooltip
        placement="top"
        title={isSubAgentAccount ? 'Add existing Sub-Agent Account' : 'Link existing sub accounts from Salesforce'}>
        <button
          className="flex space-x-2 items-center"
          onClick={() => addExistingSubAccounts()}
          data-cy="link-existing-sub-accounts">
          <Icon
            type="plus"
            className="p-2"
          />
          <TextSpan
            color="grey12"
            size="sm14">
            {isSubAgentAccount ? 'Add existing Sub-Agent Account' : 'Link Existing Sub Accounts'}
          </TextSpan>
        </button>
      </Tooltip>
    </PermissibleRender>
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Account Name',
      width: 350
    },
    {
      field: 'action',
      sortable: false,
      headerName: 'Actions',
      renderCell: (params: GridCellParams) => {
        return (
          <div className="flex space-x-3">
            <CardButton
              disabled={!!selectedAccounts.length}
              className="hover:bg-slate-200 rounded disabled:opacity-25"
              data-testid="view-account"
              icon="details"
              text="View"
              onClick={() => {
                handleDetailClick(params.row.id);
              }}
            />
            <PermissibleRender requiredPermissions={[Permission.CREATE_USERS]}>
              <CardButton
                disabled={!!selectedAccounts.length}
                data-testid="delete-account"
                className="hover:bg-slate-200 rounded disabled:opacity-25"
                icon="trash"
                text="Delete"
                onClick={() => {
                  handleDeleteClick(params.row.id);
                }}
              />
            </PermissibleRender>
          </div>
        );
      },
      hideable: false,
      flex: 1
    }
  ];

  return (
    <DetailViewCard
      collapsed={collapsed}
      dataCy="sub-accounts-card"
      title={`Sub Accounts (${subAccounts?.length || 0})`}
      cardActions={cardActions()}
      enableCollapse>
      <PermissibleRender requiredPermissions={[Permission.CREATE_USERS]}>
        <MultiSelect
          sx={{
            width: '98%',
            marginBottom: '1rem',
            marginRight: 'auto',
            marginLeft: 'auto'
          }}
          selectedValues={selectedAccounts.map((a) => a.name)}
          options={getAccountOptions()}
          handleChange={handleAccountSelect}
          inputLabel="Add Sub Accounts"
        />
      </PermissibleRender>
      <div className="overflow-y-scroll max-h-[400px]">
        <BasicDataGrid
          rows={subAccounts}
          columns={columns}
          dataCy="account-details-sub-account-tbl"
        />
      </div>
      <PermissibleRender requiredPermissions={[Permission.CREATE_USERS]}>
        <div className="text-end space-x-3 mt-4">
          <button
            disabled={!selectedAccounts.length}
            className={DEFAULT_CANCEL_BUTTON_STYLE}
            onClick={() => setSelectedAccounts([])}>
            Cancel
          </button>
          <button
            disabled={!selectedAccounts.length}
            className={DEFAULT_ADVANCE_BUTTON_STYLE}
            onClick={handleSave}>
            Save
          </button>
        </div>
      </PermissibleRender>
    </DetailViewCard>
  );
};
